import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, ShoppingCart, Trash2, CreditCard, Gamepad2 } from "lucide-react";
import gamesHeroImg from "@/assets/games-hero.png";

interface CartEntry {
  id: string;
  game_id: string;
  number_picked: number | null;
  guess_value: string | null;
  payment_status: string;
  created_at: string;
  game_name?: string;
  entry_price?: number;
}

export default function GameCart() {
  const { ptaId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<CartEntry[]>([]);
  const [ptaData, setPtaData] = useState<any>(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  useEffect(() => { fetchData(); }, [ptaId]);

  const fetchData = async () => {
    if (!ptaId) return;
    const { data: pta } = await supabase.rpc("get_public_pta_info", { p_pta_id: ptaId }).single();
    if (!pta) { setLoading(false); return; }
    setPtaData(pta);

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setLoading(false); return; }

    const { data: parent } = await supabase
      .from("parents").select("id")
      .eq("user_id", user.id).eq("pta_id", pta.id).maybeSingle();
    if (!parent) { setLoading(false); return; }

    const { data: pendingEntries } = await supabase
      .from("guessing_game_entries")
      .select("id, game_id, number_picked, guess_value, payment_status, created_at")
      .eq("parent_id", parent.id)
      .eq("payment_status", "pending")
      .order("created_at", { ascending: false });

    if (!pendingEntries?.length) { setEntries([]); setLoading(false); return; }

    const gameIds = [...new Set(pendingEntries.map(e => e.game_id))];
    const { data: games } = await supabase
      .from("guessing_games").select("id, name, entry_price").in("id", gameIds);
    const gameMap = new Map(games?.map(g => [g.id, g]) || []);

    setEntries(pendingEntries.map(e => ({
      ...e,
      game_name: gameMap.get(e.game_id)?.name || "Unknown Game",
      entry_price: gameMap.get(e.game_id)?.entry_price || 0,
    })));
    setLoading(false);
  };

  const handleRemoveEntry = async (entryId: string) => {
    setRemovingId(entryId);
    const { error } = await supabase
      .from("guessing_game_entries").delete()
      .eq("id", entryId).eq("payment_status", "pending");
    if (error) {
      toast({ title: "Error", description: "Failed to remove entry.", variant: "destructive" });
    } else {
      setEntries(prev => prev.filter(e => e.id !== entryId));
      toast({ title: "Removed", description: "Entry removed from cart." });
    }
    setRemovingId(null);
  };

  const handlePayAll = async () => {
    if (entries.length === 0) return;
    setPaymentLoading(true);
    try {
      const totalAmount = entries.reduce((sum, e) => sum + (e.entry_price || 0), 0);
      const { data, error } = await supabase.functions.invoke("create-game-entry-checkout", {
        body: { ptaId: ptaData?.id, entryIds: entries.map(e => e.id), amount: totalAmount, gameId: entries[0].game_id, gameName: "Game Entries" },
      });
      if (error) throw error;
      if (data?.url) window.location.href = data.url;
      else if (data?.error) toast({ title: "Payment error", description: data.error, variant: "destructive" });
    } catch (err: any) {
      toast({ title: "Payment error", description: err.message || "Failed to start payment", variant: "destructive" });
    }
    setPaymentLoading(false);
  };

  const totalCost = entries.reduce((sum, e) => sum + (e.entry_price || 0), 0);
  const headerColor = ptaData?.header_color || "#6366f1";

  const groupedEntries = entries.reduce<Record<string, CartEntry[]>>((acc, entry) => {
    if (!acc[entry.game_id]) acc[entry.game_id] = [];
    acc[entry.game_id].push(entry);
    return acc;
  }, {});

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><p>Loading...</p></div>;
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="h-20 flex items-center justify-between px-6" style={{ backgroundColor: headerColor }}>
        <Link to={`/fundraising/${ptaId}/games`} className="flex items-center gap-3">
          {ptaData?.logo_url ? (
            <img src={ptaData.logo_url} alt={ptaData.name} className="h-12 w-auto object-contain" />
          ) : (
            <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center"><span className="text-lg">🏫</span></div>
          )}
          <span className="font-semibold text-lg text-foreground hidden sm:inline-block">{ptaData?.name || "PTA"}</span>
        </Link>
        <Button variant="ghost" size="sm" onClick={() => navigate(`/fundraising/${ptaId}/games`)} className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Back to Games
        </Button>
      </header>

      <main className="flex-1 container py-8">
        <div className="flex flex-col lg:flex-row gap-10 items-start">
          {/* Left: Hero image */}
          <div className="hidden lg:flex flex-col items-center lg:w-5/12 sticky top-8">
            <img src={gamesHeroImg} alt="Games and competitions" className="w-full max-w-sm rounded-2xl shadow-lg" />
            <div className="mt-6 text-center space-y-2">
              <h2 className="text-xl font-bold text-foreground">Good luck!</h2>
              <p className="text-sm text-muted-foreground max-w-xs">
                Complete your payment to confirm your entries. Every entry supports our school!
              </p>
            </div>
          </div>

          {/* Right: Cart details */}
          <div className="flex-1 lg:w-7/12">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <ShoppingCart className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Your Cart</h1>
                <p className="text-sm text-muted-foreground">
                  {entries.length} {entries.length === 1 ? "entry" : "entries"} awaiting payment
                </p>
              </div>
            </div>

            {entries.length === 0 ? (
              <div className="text-center py-20 space-y-4">
                <div className="mx-auto w-20 h-20 rounded-full bg-muted flex items-center justify-center">
                  <ShoppingCart className="h-10 w-10 text-muted-foreground" />
                </div>
                <h2 className="text-xl font-semibold text-foreground">Your cart is empty</h2>
                <p className="text-muted-foreground">Play some games to add entries!</p>
                <Button onClick={() => navigate(`/fundraising/${ptaId}/games`)} className="gap-2">
                  <Gamepad2 className="h-4 w-4" /> Browse Games
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(groupedEntries).map(([gameId, gameEntries]) => (
                  <Card key={gameId}>
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-foreground">{gameEntries[0].game_name}</h3>
                        <Badge variant="outline" className="text-xs">£{(gameEntries[0].entry_price || 0).toFixed(2)} each</Badge>
                      </div>
                      <div className="space-y-2">
                        {gameEntries.map(entry => (
                          <div key={entry.id} className="flex items-center justify-between py-1.5 px-3 rounded-md bg-muted/50">
                            <span className="text-sm text-foreground">
                              {entry.number_picked != null ? `Number #${entry.number_picked}` : entry.guess_value}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">£{(entry.entry_price || 0).toFixed(2)}</span>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleRemoveEntry(entry.id)} disabled={removingId === entry.id}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}

                <Card className="border-primary/30">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total</p>
                      <p className="text-2xl font-bold text-foreground">£{totalCost.toFixed(2)}</p>
                    </div>
                    <Button size="lg" onClick={handlePayAll} disabled={paymentLoading} className="gap-2">
                      <CreditCard className="h-4 w-4" />
                      {paymentLoading ? "Redirecting..." : `Pay £${totalCost.toFixed(2)}`}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </main>

      <footer className="py-6 text-center text-sm text-muted-foreground border-t border-border">
        <p>Powered by <span className="font-semibold text-foreground">The PTA Hub</span></p>
      </footer>
    </div>
  );
}
